import type {
  NormRect,
  NormCircle,
  NormPolyline,
  NormPoint,
  ShapeGeometry,
} from "@modules/docs/annotations/types/TAnnotation";

// Minimum bounding-box side length (in unscaled page px) for a shape to be committed.
export const MIN_BOX_SIZE = 6;

// ─── NormRect ─────────────────────────────────────────────────────────────────

/**
 * Convert local page pixel coords (unscaled) to NormRect.
 * x1/x2 are normalized by pageWidth; y1/y2 by pageHeight.
 * Coordinates are sorted so x1 ≤ x2 and y1 ≤ y2.
 */
export function toNormRect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  pageWidth: number,
  pageHeight: number,
): NormRect {
  return {
    x1: Math.min(x1, x2) / pageWidth,
    y1: Math.min(y1, y2) / pageHeight,
    x2: Math.max(x1, x2) / pageWidth,
    y2: Math.max(y1, y2) / pageHeight,
  };
}

/**
 * Convert NormRect back to local page pixel coords (unscaled).
 */
export function fromNormRect(
  rect: NormRect,
  pageWidth: number,
  pageHeight: number,
): { x1: number; y1: number; x2: number; y2: number } {
  return {
    x1: rect.x1 * pageWidth,
    y1: rect.y1 * pageHeight,
    x2: rect.x2 * pageWidth,
    y2: rect.y2 * pageHeight,
  };
}

// ─── NormCircle ───────────────────────────────────────────────────────────────

/**
 * Convert a drawn bounding box (unscaled page px) to NormCircle.
 * cx/cy are normalized to pageWidth/pageHeight respectively.
 * r is the half-width of the bounding box normalized to pageWidth,
 * making it a circle in page-normalized space.
 */
export function toNormCircleFromRect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  pageWidth: number,
  pageHeight: number,
): NormCircle {
  const minX = Math.min(x1, x2);
  const maxX = Math.max(x1, x2);
  const minY = Math.min(y1, y2);
  const maxY = Math.max(y1, y2);
  return {
    cx: (minX + maxX) / 2 / pageWidth,
    cy: (minY + maxY) / 2 / pageHeight,
    r: (maxX - minX) / 2 / pageWidth,
  };
}

/**
 * Convert NormCircle back to a bounding box (unscaled page px).
 * rx = r * pageWidth; ry = r * pageWidth (square bounding box for a true circle).
 */
export function fromNormCircleToRect(
  circle: NormCircle,
  pageWidth: number,
  pageHeight: number,
): { x1: number; y1: number; x2: number; y2: number } {
  const pxCx = circle.cx * pageWidth;
  const pxCy = circle.cy * pageHeight;
  const pxR = circle.r * pageWidth;
  return {
    x1: pxCx - pxR,
    y1: pxCy - pxR,
    x2: pxCx + pxR,
    y2: pxCy + pxR,
  };
}

// ─── NormPolyline ─────────────────────────────────────────────────────────────

/**
 * Convert pixel points (unscaled page-local) to NormPolyline.
 * x normalized by pageWidth, y by pageHeight.
 */
export function toNormPolylineFromPoints(
  points: NormPoint[],
  pageWidth: number,
  pageHeight: number,
): NormPolyline {
  return {
    points: points.map((p) => ({
      x: p.x / pageWidth,
      y: p.y / pageHeight,
    })),
  };
}

/**
 * Convert NormPolyline back to pixel points (unscaled page-local).
 */
export function fromNormPolylineToPoints(
  polyline: NormPolyline,
  pageWidth: number,
  pageHeight: number,
): NormPoint[] {
  return polyline.points.map((p) => ({
    x: p.x * pageWidth,
    y: p.y * pageHeight,
  }));
}

// ─── geometryToRect ───────────────────────────────────────────────────────────

/**
 * Convert any ShapeGeometry to a bounding box {x, y, w, h} in unscaled page-local px.
 * x, y = top-left corner; w, h = dimensions.
 */
export function geometryToRect(
  geometry: ShapeGeometry,
  pageWidth: number,
  pageHeight: number,
): { x: number; y: number; w: number; h: number } {
  if ("x1" in geometry) {
    const { x1, y1, x2, y2 } = fromNormRect(geometry, pageWidth, pageHeight);
    return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
  }

  if ("cx" in geometry) {
    const { x1, y1, x2, y2 } = fromNormCircleToRect(geometry, pageWidth, pageHeight);
    return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
  }

  // NormPolyline — bounding box of all points
  const pts = fromNormPolylineToPoints(geometry, pageWidth, pageHeight);
  if (pts.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  return {
    x: Math.min(...xs),
    y: Math.min(...ys),
    w: Math.max(...xs) - Math.min(...xs),
    h: Math.max(...ys) - Math.min(...ys),
  };
}
